import { cn } from '@/lib/utils/cn';
import { QuestionWrapper } from './question-wrapper';
import type { QuestionComponentProps } from '@/types';

type RatingStyle = 'star' | 'emoji' | 'heart' | 'thumb' | 'numeric';

interface RatingConfig {
  maxValue?: number;
  minValue?: number;
  showLabels?: boolean;
  minLabel?: string;
  maxLabel?: string;
  scaleType?: RatingStyle;
  activeColor?: string;
  showValue?: boolean;
}

// SVG icons for different rating styles
const StarIcon = ({ filled, color }: { filled: boolean; color: string }) => (
  <svg
    className={cn('h-8 w-8 sm:h-10 sm:w-10 transition-colors', filled ? color : 'text-gray-300')}
    fill={filled ? 'currentColor' : 'none'}
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={filled ? 0 : 1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
    />
  </svg>
);

const HeartIcon = ({ filled, color }: { filled: boolean; color: string }) => (
  <svg
    className={cn('h-8 w-8 sm:h-10 sm:w-10 transition-colors', filled ? color : 'text-gray-300')}
    fill={filled ? 'currentColor' : 'none'}
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={filled ? 0 : 1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
    />
  </svg>
);

const ThumbIcon = ({ filled, color }: { filled: boolean; color: string }) => (
  <svg
    className={cn('h-8 w-8 sm:h-10 sm:w-10 transition-colors', filled ? color : 'text-gray-300')}
    fill={filled ? 'currentColor' : 'none'}
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={filled ? 0 : 1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75A2.25 2.25 0 0116.5 4.5c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 00-1.423-.23H5.904M14.25 9h2.25M5.904 18.75c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 01-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 10.203 4.167 9.75 5 9.75h1.053c.472 0 .745.556.5.96a8.958 8.958 0 00-1.302 4.665c0 1.194.232 2.333.654 3.375z"
    />
  </svg>
);

// Emoji sets for different scale ranges
const getEmojiSet = (count: number): string[] => {
  if (count <= 3) return ['😞', '😐', '😊'];
  if (count <= 5) return ['😡', '😞', '😐', '😊', '😍'];
  if (count <= 7) return ['😡', '😠', '😞', '😐', '😊', '😄', '😍'];
  return ['😡', '😠', '😞', '🙁', '😐', '🙂', '😊', '😄', '🤩', '😍'].slice(0, count);
};

// Color mappings
const getStyleColor = (style: RatingStyle, customColor?: string): string => {
  if (customColor) return customColor;
  switch (style) {
    case 'star': return 'text-yellow-400';
    case 'heart': return 'text-red-500';
    case 'thumb': return 'text-blue-500';
    case 'emoji': return 'text-yellow-500';
    case 'numeric': return 'text-primary-600';
    default: return 'text-yellow-400';
  }
};

export function Rating({
  question,
  value,
  onChange,
  disabled,
  errors,
}: QuestionComponentProps<number | null>) {
  const config = question.config as RatingConfig | undefined;

  const maxValue = config?.maxValue || 5;
  const minValue = config?.minValue || 1;
  const showLabels = config?.showLabels ?? true;
  const minLabel = config?.minLabel;
  const maxLabel = config?.maxLabel;
  const style: RatingStyle = config?.scaleType || 'star';
  const customColor = config?.activeColor;
  const showValue = config?.showValue ?? true;

  const handleSelect = (rating: number) => {
    if (disabled) return;
    // Allow deselection by clicking the same value
    if (value === rating) {
      onChange(null);
    } else {
      onChange(rating);
    }
  };

  const totalItems = maxValue - minValue + 1;
  const color = getStyleColor(style, customColor);
  const emojis = style === 'emoji' ? getEmojiSet(totalItems) : [];

  const renderRatingItem = (index: number) => {
    const itemValue = minValue + index;
    const isSelected = value !== null && itemValue <= value;
    const isExactValue = value === itemValue;

    switch (style) {
      case 'emoji':
        return (
          <span
            className={cn(
              'text-3xl sm:text-4xl transition-all',
              isExactValue ? 'scale-125' : 'grayscale opacity-50',
              !disabled && 'hover:scale-110 hover:grayscale-0 hover:opacity-100'
            )}
          >
            {emojis[index] || '😐'}
          </span>
        );

      case 'heart':
        return <HeartIcon filled={isSelected} color={color} />;

      case 'thumb':
        return <ThumbIcon filled={isSelected} color={color} />;

      case 'numeric':
        return (
          <div
            className={cn(
              'w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-lg font-semibold transition-colors border-2',
              isExactValue
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-600 border-gray-300',
              !disabled && !isExactValue && 'hover:border-primary-400 hover:text-primary-600'
            )}
          >
            {itemValue}
          </div>
        );

      case 'star':
      default:
        return <StarIcon filled={isSelected} color={color} />;
    }
  };

  return (
    <QuestionWrapper question={question} errors={errors}>
      <div className="space-y-3">
        {/* Rating items */}
        <div className="flex gap-1 flex-wrap justify-center sm:justify-start">
          {Array.from({ length: totalItems }, (_, i) => {
            const itemValue = minValue + i;

            return (
              <button
                key={itemValue}
                type="button"
                onClick={() => handleSelect(itemValue)}
                disabled={disabled}
                className={cn(
                  'p-1 transition-transform focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded',
                  disabled && 'opacity-50 cursor-not-allowed hover:scale-100',
                  !disabled && 'hover:scale-110'
                )}
                aria-label={`Rate ${itemValue} out of ${maxValue}`}
              >
                {renderRatingItem(i)}
              </button>
            );
          })}
        </div>

        {/* Labels */}
        {showLabels && (minLabel || maxLabel) && (
          <div className="flex justify-between text-xs text-gray-500 px-1">
            <span>{minLabel}</span>
            <span>{maxLabel}</span>
          </div>
        )}

        {/* Current value indicator */}
        {showValue && value !== null && (
          <p className="text-sm text-gray-600 text-center sm:text-left">
            You selected: <span className="font-medium">{value}</span> out of {maxValue}
          </p>
        )}
      </div>
    </QuestionWrapper>
  );
}
